import { Button, ScrollView, SelectBox } from "devextreme-react";
import { useTranslation } from "react-i18next";
import { AbsenceConfirmPopup2 } from "./components/AbsenceConfirmPopup";
import { AbsenceCreatePopup2 } from "./components/AbsenceCreatePopup";
import { AllEmployeesTableView2 } from "./components/AllEmployeesTableView";
import { CalendarLegend2 } from "./components/CalendarLegend";
import { EmployeeCalendarView2 } from "./components/EmployeeCalendarView";
import { useAbsenceActions } from "./hooks/useAbsenceActions";
import { useAbsenceConfirmation } from "./hooks/useAbsenceConfirmation";
import { useHoliday2Data } from "./hooks/useHoliday2Data";
import "./Holiday2.scss";

export const Holiday2 = () => {
  const { t } = useTranslation();
  const data = useHoliday2Data();

  const actions = useAbsenceActions({
    currentMonth: data.currentMonth,
    viewMode: data.viewMode,
    isForeman: data.isForeman,
    isHR: data.isHR,
    holidayData: data.holidayData,
    users: data.users,
    setUsers: data.setUsers as (fn: (prev: any[]) => any[]) => void,
    names: data.names,
    fetchAbsences: data.fetchAbsences,
    fetchAllEmployeesData: data.fetchAllEmployeesData,
    employmentIndex: data.employmentIndex,
  });

  const confirmation = useAbsenceConfirmation(data.currentMonth);

  const showViewToggle = data.isNotEmployee || data.isTimeManager;
  const showConfirmButton = data.isNotEmployee && data.isNotForeman;
  const showLegend = data.viewMode === "Employee" || (data.viewMode === "AllEmployees" && data.entries.length > 0);

  return (
    <ScrollView useNative>
      <div className="p-6">
        {/* Header */}
        <div className="mb-5 flex flex-col md:flex-row gap-2 justify-between items-start">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">{t("Leave")}</div>
            <div className="text-base">{t("holidayPopup.headerText")}</div>
          </div>
          {showViewToggle && (
            <SelectBox
              width={150}
              dataSource={[
                { value: "Employee", text: t("Employee") },
                { value: "AllEmployees", text: t("AllEmployees") },
              ]}
              value={data.viewMode}
              displayExpr="text"
              valueExpr="value"
              onValueChanged={(e) => data.setViewMode(e.value)}
              label={t("view")}
              labelMode="static"
            />
          )}
        </div>

        {/* Confirm button */}
        {showConfirmButton && (
          <Button
            text={t("holidayPopup.confirmHoliday")}
            type="default"
            onClick={confirmation.handleOpenConfirmPopup}
            icon={confirmation.hasNewData ? "tips" : undefined}
            className={`mb-6 presence-button ${confirmation.filteredSlots.length > 0 ? "presence-button--red" : ""}`}
          />
        )}

        {/* Employee view */}
        {data.viewMode === "Employee" && (
          <EmployeeCalendarView2
            currentMonth={data.currentMonth}
            setCurrentMonth={data.setCurrentMonth}
            schedule={data.schedule}
            holidayData={data.holidayData}
            holidayLimitUser={data.holidayLimitUser}
            isLoading={data.isLoading}
            onDoubleClick={actions.handleDateClick}
            lastCellClickTime={actions.lastCellClickTime}
            employmentIndex={data.employmentIndex}
          />
        )}

        {/* AllEmployees view */}
        {data.viewMode === "AllEmployees" && (
          <AllEmployeesTableView2
            currentMonth={data.currentMonth}
            setCurrentMonth={data.setCurrentMonth}
            entries={data.entries}
            totalCount={data.totalCount}
            pageIndex={data.pageIndex}
            setPageIndex={data.setPageIndex}
            pageSize={data.pageSize}
            setPageSize={data.setPageSize}
            gridSearchQuery={data.gridSearchQuery}
            setGridSearchQuery={data.setGridSearchQuery}
            holidayData={data.holidayData}
            isLoading={data.isLoading}
            isForeman={data.isForeman}
            isHR={data.isHR}
            onCellDoubleClick={actions.handleDateClick}
            lastCellClickTime={actions.lastCellClickTime}
            employmentIndex={data.employmentIndex}
          />
        )}

        {/* Create absence popup */}
        <AbsenceCreatePopup2
          visible={actions.popupVisible}
          onHiding={actions.handleCancel}
          onSubmit={actions.handleSubmitAbsence}
          viewMode={data.viewMode}
          isNotEmployee={data.isNotEmployee}
          isForeman={data.isForeman}
          isTimeManager={data.isTimeManager}
          users={data.users}
          names={data.names}
          selectedDate={actions.selectedDate}
          setSelectedDate={actions.setSelectedDate}
          selectedEndDate={actions.selectedEndDate}
          setSelectedEndDate={actions.setSelectedEndDate}
          absenceName={actions.absenceName}
          setAbsenceName={actions.setAbsenceName}
          notes={actions.notes}
          setNotes={actions.setNotes}
          skipDaysOff={actions.skipDaysOff}
          setSkipDaysOff={actions.setSkipDaysOff}
          leaveForAssignment={actions.leaveForAssignment}
          setLeaveForAssignment={actions.setLeaveForAssignment}
          userSend={actions.userSend}
          setUserSend={actions.setUserSend}
          entriesEmployeeId={actions.entriesEmployeeId}
          onlyWeekendDays={actions.onlyWeekendDays}
          cancelAbsenceData={actions.cancelAbsenceData}
          cancelRequestPending={actions.cancelRequestPending}
          onCancelAbsence={actions.handleAbsenceCancel}
          isSubmitting={actions.isSubmitting}
          editableAbsenceId={actions.editableAbsenceId}
          onDeleteOwnAbsence={actions.handleDeleteOwnAbsence}
          hourFrom={actions.hourFrom}
          setHourFrom={actions.setHourFrom}
          hourTo={actions.hourTo}
          setHourTo={actions.setHourTo}
          isHourlyAbsence={actions.isHourlyAbsence}
          canBeHourly={actions.canBeHourly}
          addByHour={actions.addByHour}
          setAddByHour={actions.setAddByHour}
        />

        {/* Confirm absence popup */}
        <AbsenceConfirmPopup2
          visible={confirmation.visiblePopupHoliday}
          onHiding={() => {
            confirmation.setVisiblePopupHoliday(false);
            data.fetchAllEmployeesData(data.currentMonth);
          }}
          filteredSlots={confirmation.filteredSlots}
          isHR={data.isHR}
          departments={confirmation.departments}
          selectedDepartment={confirmation.selectedDepartment}
          setSelectedDepartment={confirmation.setSelectedDepartment}
          searchQuery={confirmation.searchQuery}
          setSearchQuery={confirmation.setSearchQuery}
          onConfirm={confirmation.handleConfirmAbsence}
          onReject={confirmation.handleRejectAbsence}
          onConfirmAll={confirmation.handleConfirmAll}
          onRejectAll={confirmation.handleRejectAll}
        />

        {/* Legend */}
        {showLegend && <CalendarLegend2 />}
      </div>
    </ScrollView>
  );
};
